/**
 * Helpers for rendering EXPORT_READY_FOR_DOWNLOAD notification descriptions.
 * Extracted so the description-processing logic can be unit-tested without
 * mounting the host Vue components (HERA-8159).
 *
 * The original presigned download URL baked into a notification's description
 * is signed with the producing Lambda's temporary STS credentials, so it
 * expires (~12h) long before the notification's 7-day TTL. New-format
 * notifications carry an `s3Key` in their payload; the frontend regenerates a
 * fresh URL on click via rdsApi /downloadS3File. For those, the stale inline
 * <a href> must be neutralized so a click routes through regeneration instead
 * of opening the dead link (which returns an S3 ExpiredToken error).
 */

// Matches an opening anchor tag with any attributes, e.g. <a href="..." download="...">
const ANCHOR_OPEN_TAG = /<a\b[^>]*>/gi;

/**
 * Safely parse a notification's JSON payload. Returns {} on any error.
 * @param {{payload?: string}} notification
 * @returns {object}
 */
export function parseNotificationPayload(notification) {
  try {
    return JSON.parse(notification.payload) || {};
  } catch (e) {
    return {};
  }
}

/**
 * Process an export notification's description HTML for safe rendering.
 *
 * - New-format (payload.s3Key present): strip attributes from inline anchors so
 *   a click bubbles to the row handler, which regenerates a fresh presigned URL.
 * - Legacy (no s3Key) with legacyStopPropagation=true: keep the inline link but
 *   add onclick="event.stopPropagation()" so it does not trigger the
 *   (non-clickable) row handler.
 * - Otherwise: return the description unchanged.
 *
 * @param {{payload?: string, description?: string}} notification
 * @param {{legacyStopPropagation?: boolean}} [options]
 * @returns {string}
 */
export function processExportNotificationDescription(notification, options = {}) {
  const html = notification.description || '';
  const payload = parseNotificationPayload(notification);
  if (payload && payload.s3Key) {
    return html.replace(ANCHOR_OPEN_TAG, '<a>');
  }
  if (options.legacyStopPropagation) {
    return html.replace(/<a /g, '<a onclick="event.stopPropagation()" ');
  }
  return html;
}
